import * as Linking from 'expo-linking';
import * as Location from 'expo-location';
import i18n from '@/utils/i18n';
import LeadGeneratorMapPin from '@assets/icons/lead-generator-map-pin.svg';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SearchIcon from '@assets/icons/search.svg';
import { ActionSheet } from '@/components/action-sheet';
import { Analytics } from '@/utils/analytics';
import {
  AppContainerStackParams,
  LocationManagerStackParams,
} from '@/navigators/types';
import {
  CONTAINER_MODAL_ROUTES,
  LOCATION_MANAGER_ROUTES,
  ORDER_INBOUNDS_ROUTES,
} from '@/navigators/routes';
import { createStyles } from '@/contexts/theme-context';
import { FilterPills } from '@/components/filter-pills';
import {
  LocationOption,
  LocationPickerList,
} from '@/components/locations/location-picker-list';
import { ModalContainer } from '@/components/modal-container';
import { MultiSelectFilterType } from '@/hooks/use-location-picker-filter-pills';
import { MultiStateCheckbox } from '@/components/multi-state-checkbox';
import { OfflineDownloadHeader } from '@/components/locations/offline-download-header';
import { OfflineManagementActions } from '@/components/locations/offline-management-actions';
import { OfflineManagementButton } from '@/components/locations/offline-management-button';
import { OrderInboundsIcon } from '@/components/locations/order-inbounds-icon';
import { PillButton, PillButtonType } from '@/components/pill-button';
import { Platform, TouchableOpacity, View } from 'react-native';
import { RecyclerListRef } from '@/components/recycler-list';
import { SearchFiltersActionSheet } from '@/components/search-filters/search-filters-action-sheet';
import { SearchInput } from '@/components/search-input';
import { SIZES } from '@/components/button';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLocationPickerData } from '@/hooks/use-location-picker-data';
import { useNavigation } from '@react-navigation/native';
import {
  useOfflineDownloadManagementContext,
  useSelectedOfflineRestaurantUUIDs,
} from '@/contexts/offline-download-management-context';
import { useOfflineModeEnabled } from '@/hooks/use-offline-mode-enabled';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type Props = StackScreenProps<
  LocationManagerStackParams,
  LOCATION_MANAGER_ROUTES.Base
>;

export const LocationPickerScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const {
    restaurantRelationships,
    selectedRestaurantRelationship,
    selectedRestaurantUUID,
    user: { uuid: userUUID },
  } = useAuthenticatedUserContext();
  const { supplierUUID, showProspectFinder } = selectedRestaurantRelationship;
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const listRef = useRef<RecyclerListRef<LocationOption>>(null);

  const {
    accountModeFilter,
    clearSearch,
    dataStatus,
    filteredRestaurantRelationships,
    getPillRef,
    hasMultipleSuppliers,
    multiSelectFilters,
    onDisplayTextChange,
    pillButtonProps,
    refetchDataStatus,
    searching,
    searchText,
    syncedRestaurantUUIDs,
  } = useLocationPickerData(restaurantRelationships);

  const { navigate } =
    useNavigation<
      StackNavigationProp<
        AppContainerStackParams,
        CONTAINER_MODAL_ROUTES.OrderInbounds
      >
    >();
  const { isOrderInboundsEnabled } = useOrderInbounds();

  const {
    cancelDownload,
    downloadProgress,
    resetDownloadProgress,
    resetSelection,
    selectAllRestaurants,
    toggleRestaurantSelection,
  } = useOfflineDownloadManagementContext((s) => ({
    cancelDownload: s.cancelDownload,
    downloadProgress: s.downloadProgress,
    resetDownloadProgress: s.resetDownloadProgress,
    resetSelection: s.resetSelection,
    selectAllRestaurants: s.selectAllRestaurants,
    toggleRestaurantSelection: s.toggleRestaurantSelection,
  }));
  const selectedOfflineRestaurantUUIDs = useSelectedOfflineRestaurantUUIDs();

  const [isManagingOfflineAccounts, setIsManagingOfflineAccounts] =
    useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [openLeadGen, setOpenLeadGen] = useState(false);
  const [permissionResponse, requestLocationPermission] =
    Location.useForegroundPermissions();

  const renderPillButton = useCallback((button: PillButtonType) => {
    return <PillButton button={button} key={button.label} ref={button.ref} />;
  }, []);

  useEffect(() => {
    if (!isManagingOfflineAccounts) {
      resetSelection();
    }
  }, [isManagingOfflineAccounts, resetSelection]);

  const handleScrollToTop = () => {
    if (listRef.current) {
      listRef.current.scrollTo({ animated: true, y: 0 });
    }
  };

  const navigationHeaderProps = useMemo(() => {
    const filteredRestaurantUUIDs = new Set(
      filteredRestaurantRelationships.map((rr) => rr.uuid),
    );
    const intersection = new Set(
      [...filteredRestaurantUUIDs].filter((x) =>
        selectedOfflineRestaurantUUIDs.has(x),
      ),
    );
    const isPartiallySelected =
      intersection.size > 0 &&
      intersection.size !== filteredRestaurantUUIDs.size;

    return {
      actions: [
        ...(isOrderInboundsEnabled && Platform.OS === 'web'
          ? [
              {
                component: (
                  <TouchableOpacity
                    onPress={() => {
                      navigate(CONTAINER_MODAL_ROUTES.OrderInbounds, {
                        screen: ORDER_INBOUNDS_ROUTES.List,
                      });
                      Analytics.track('location_picker.order_inbounds.press');
                    }}
                  >
                    <OrderInboundsIcon iconColor={theme.colors.primary} />
                  </TouchableOpacity>
                ),
              },
            ]
          : []),
        ...(isOfflineModeEnabled
          ? [
              {
                component: (
                  <OfflineManagementButton
                    isDownloading={Boolean(downloadProgress)}
                    isManagingOfflineAccounts={isManagingOfflineAccounts}
                    setIsManagingOfflineAccounts={setIsManagingOfflineAccounts}
                  />
                ),
              },
            ]
          : []),
        {
          Icon: SearchIcon,
          iconProps: {
            height: 20,
            width: 20,
          },
          onPress: () => {
            if (showSearch) {
              clearSearch();
              setShowSearch(false);
            } else {
              setShowSearch(true);
            }
          },
          testID: 'location-search-icon',
        },
      ],
      onHeaderPress: handleScrollToTop,
      showShadow: true,
      subheader: (
        <>
          {showSearch ? (
            <View style={styles.searchContainer}>
              <SearchInput
                autoCorrect={false}
                autoFocus
                containerStyle={styles.searchInput}
                onChangeText={onDisplayTextChange}
                placeholder={i18n.t('search')}
                testID="location-picker-input"
                value={searchText}
              />
            </View>
          ) : null}
          {filteredRestaurantRelationships.length > 0 ? (
            <View collapsable={false}>
              {multiSelectFilters.map((filter) => (
                <SearchFiltersActionSheet<MultiSelectFilterType>
                  buttonRef={getPillRef(filter.key)}
                  filter={filter}
                  isVisible={filter.isSelecting}
                  key={filter.key}
                  onClose={filter.onClose}
                  onSelect={(_key, selectedValues) => {
                    filter.setSelected(selectedValues);
                    filter.onClose();
                  }}
                  trackingPrefix="accounts_selector"
                />
              ))}
              <FilterPills
                buttons={pillButtonProps}
                renderPillButton={renderPillButton}
              />
            </View>
          ) : null}
          {downloadProgress ? (
            <OfflineDownloadHeader
              cancelDownload={cancelDownload}
              resetDownloadProgress={resetDownloadProgress}
            />
          ) : isManagingOfflineAccounts ? (
            <View style={styles.selectAllContainer}>
              <Typography variant="heading-sm">
                {i18n.t('select_all')}
              </Typography>
              <MultiStateCheckbox
                isPartiallySelected={isPartiallySelected}
                isSelected={selectedOfflineRestaurantUUIDs.size > 0}
                onSelect={() =>
                  selectAllRestaurants(filteredRestaurantRelationships)
                }
                trackingData={null}
              />
            </View>
          ) : null}
        </>
      ),
      title: i18n.t('accounts'),
    };
  }, [
    cancelDownload,
    clearSearch,
    downloadProgress,
    filteredRestaurantRelationships,
    getPillRef,
    isManagingOfflineAccounts,
    isOfflineModeEnabled,
    isOrderInboundsEnabled,
    multiSelectFilters,
    navigate,
    onDisplayTextChange,
    pillButtonProps,
    renderPillButton,
    resetDownloadProgress,
    searchText,
    selectAllRestaurants,
    selectedOfflineRestaurantUUIDs,
    showSearch,
    styles,
    theme,
  ]);

  const bottomActions = useMemo(
    () =>
      Platform.OS === 'web'
        ? []
        : [
            ...(isOrderInboundsEnabled
              ? [
                  {
                    component: (
                      <TouchableOpacity
                        onPress={() => {
                          navigate(CONTAINER_MODAL_ROUTES.OrderInbounds, {
                            screen: ORDER_INBOUNDS_ROUTES.List,
                          });
                          Analytics.track(
                            'location_picker.order_inbounds.press',
                          );
                        }}
                        style={styles.orderInboundsIcon}
                      >
                        <OrderInboundsIcon />
                      </TouchableOpacity>
                    ),
                  },
                ]
              : []),
            ...(showProspectFinder
              ? [
                  {
                    Icon: LeadGeneratorMapPin,
                    iconProps: {
                      fill: theme.sk.color.icon.primary,
                    },
                    onPress: async () => {
                      Analytics.track('location_picker.prospect_finder.press', {
                        restaurantUUID: selectedRestaurantUUID,
                        supplierUUID,
                        userUUID,
                      });

                      // If permission is already granted, navigate directly
                      if (
                        permissionResponse?.status ===
                        Location.PermissionStatus.GRANTED
                      ) {
                        Analytics.track(
                          'location_picker.prospect_finder.location_permission_result',
                          {
                            canAskAgain: permissionResponse.canAskAgain,
                            granted: permissionResponse.granted,
                            path: 'fast',
                            status: permissionResponse.status,
                            supplierUUID,
                          },
                        );
                        navigation.navigate(
                          LOCATION_MANAGER_ROUTES.LeadGenerator,
                        );
                        return;
                      }

                      // Otherwise request permission and handle the result
                      Analytics.track(
                        'location_picker.prospect_finder.location_permission_requested',
                        {
                          canAskAgain: permissionResponse?.canAskAgain ?? null,
                          currentPermissionStatus:
                            permissionResponse?.status ?? null,
                          platform: Platform.OS,
                          supplierUUID,
                        },
                      );
                      const response = await requestLocationPermission();
                      setOpenLeadGen(true);

                      Analytics.track(
                        'location_picker.prospect_finder.location_permission_result',
                        {
                          canAskAgain: response?.canAskAgain ?? null,
                          granted: response?.granted ?? null,
                          path: 'request',
                          status: response?.status ?? null,
                          supplierUUID,
                        },
                      );

                      if (
                        response?.status === Location.PermissionStatus.GRANTED
                      ) {
                        setOpenLeadGen(false);
                        navigation.navigate(
                          LOCATION_MANAGER_ROUTES.LeadGenerator,
                        );
                      }
                    },
                  },
                ]
              : []),
          ],
    [
      isOrderInboundsEnabled,
      navigate,
      navigation,
      permissionResponse,
      requestLocationPermission,
      selectedRestaurantUUID,
      showProspectFinder,
      supplierUUID,
      theme,
      userUUID,
      styles,
    ],
  );

  const showAbsoluteBlurFooter =
    isManagingOfflineAccounts &&
    selectedOfflineRestaurantUUIDs.size > 0 &&
    !downloadProgress;

  /*
   * The height of the absolute positioned footer.
   * theme.sk.spacing.spaceLarge + bottom are vertical paddings
   * SIZES.medium * 2 + 8 is the height of two buttons in it and a gap between them
   * */
  const absoluteBlurFooterHeight =
    SIZES.medium * 2 + 8 + theme.sk.spacing.spaceLarge + bottom;

  return (
    <ModalContainer navigationHeaderProps={navigationHeaderProps}>
      <LocationPickerList
        accountModeFilter={accountModeFilter}
        bottomActions={bottomActions}
        chatUnreadCountMap={route.params.chatUnreadCountMap}
        contentContainerStyle={
          // because it covers the last element in the list
          showAbsoluteBlurFooter
            ? {
                paddingBottom: absoluteBlurFooterHeight,
              }
            : null
        }
        dataStatus={dataStatus}
        hasMultipleSuppliers={hasMultipleSuppliers}
        isMultiSelecting={isManagingOfflineAccounts}
        listRef={listRef}
        onCreateAccount={() => {
          navigation.navigate(LOCATION_MANAGER_ROUTES.CreateAccount, {
            initialName: searchText,
          });
        }}
        onRestaurantSelected={() => navigation.goBack()}
        onViewAccount={(option) => {
          navigation.navigate(LOCATION_MANAGER_ROUTES.ViewAccount, {
            onRemoveRestaurant: option.metadata?.onRemoveRestaurant,
            restaurantName: option.title,
            restaurantUUID: String(option.value),
            supplierUUID: option.metadata?.relationship.supplierUUID,
          });
        }}
        restaurantRelationships={filteredRestaurantRelationships}
        restaurantsWithNewInvoices={route.params.restaurantsWithNewInvoices}
        searching={searching}
        selectAccount={toggleRestaurantSelection}
        selectedAccountUUIDs={selectedOfflineRestaurantUUIDs}
      />
      <ActionSheet
        actions={
          // On apps, we can link the users to the app settings to allow
          // location permission, but on web we'll do something different
          Platform.OS === 'web'
            ? []
            : [i18n.t('open_settings'), i18n.t('cancel')]
        }
        isVisible={
          permissionResponse?.status === Location.PermissionStatus.DENIED &&
          openLeadGen
        }
        message={i18n.t('location_permission_sheet_message')}
        onAction={(action) => {
          if (action === i18n.t('open_settings')) {
            Linking.openSettings();
          } else {
            setOpenLeadGen(false);
          }
        }}
        onClose={() => setOpenLeadGen(false)}
        title={i18n.t('location_permission_sheet_title')}
      />
      {showAbsoluteBlurFooter ? (
        <OfflineManagementActions
          onAction={() => setIsManagingOfflineAccounts(false)}
          syncedRestaurantUUIDs={syncedRestaurantUUIDs}
          updateDataStatus={refetchDataStatus}
        />
      ) : null}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  orderInboundsIcon: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  searchContainer: {
    paddingBottom: 8,
    paddingHorizontal: 16,
  },
  searchInput: {
    backgroundColor: theme.colors.background,
  },
  selectAllContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 14,
  },
}));
